import React, { useMemo, useState, useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Button } from "react-bootstrap";
import Screen from '../components/Screen';
import { getCDNImage } from '../globals';
import { i18nValue } from '../functions';
import { SmartLock as SmartLockIcon } from "../components/Icons";
import { Player } from '@lottiefiles/react-lottie-player';
import ReactSwipeButton from 'react-swipe-button';
import { HotelContext } from "../context/hotel";
import { UserContext } from "../context/user";
import { useSmartLocks } from "../hooks/useSmartLocks";
import { T } from "react-translator-component";
import { CheckInContext } from "../context/checkin";
import { isHostzArosa, isDemo } from "../components/CheckIn/hotelConditions";

const KEYBOX_PLACEHOLDER_KEYS = ['UNIT-KEYBOX-CODE', 'UNIT-LOCKBOX-CODE'];

export const SMART_LOCK_VIEW = {
  LOADING: 'loading',
  SMART_LOCK: 'smart_lock',
  KEYBOX_ONLY: 'keybox_only',
  EMPTY: 'empty',
};

const stripStyles = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  tempDiv.querySelectorAll('style').forEach((style) => style.remove());
  tempDiv.querySelectorAll('[style]').forEach((el) => el.removeAttribute('style'));
  return tempDiv.innerHTML;
};

const getPlaceholderValue = (placeholders, phKey, language) => {
  let ph_value = '--';
  if (placeholders?.[phKey]) {
    const ph_values = placeholders[phKey];
    if (ph_values?.[language]) {
      ph_value = ph_values[language];
    } else if (ph_values?.['en-US']) {
      ph_value = ph_values['en-US'];
    }
  }
  return ph_value;
};

export const resolveKeyboxCode = (placeholders, language) => {
  if (!placeholders) return null;
  for (const key of KEYBOX_PLACEHOLDER_KEYS) {
    if (!placeholders[key]) continue;
    const val = getPlaceholderValue(placeholders, key, language);
    if (!val || val === '--') continue;
    const stripped = stripStyles(val).replace(/<[^>]*>/g, '').trim();
    if (stripped) return stripped;
  }
  return null;
};

/**
 * Estados posibles de la pantalla Smart Lock:
 * - loading: esperando API de cerraduras (solo si la habitación tiene smart locks configurados y aún no hay datos)
 * - smart_lock: PIN de cerradura y/o slider para abrir (foto 2)
 * - keybox_only: código de keybox sin smart lock (foto 1)
 * - empty: sin código ni smart lock (foto 3)
 */
export const resolveSmartLockViewState = ({
  smartLocksAll,
  smartLockPinCode,
  keyboxCode,
  hasSmartLocksConfigured,
}) => {
  const hasPin = Boolean(smartLockPinCode);
  const hasKeybox = Boolean(keyboxCode);
  const smartLocksLoaded = smartLocksAll != null;
  const unlockableLocks = smartLocksLoaded && Array.isArray(smartLocksAll)
    ? smartLocksAll.filter((s) => s.allowUnlock)
    : [];
  const hasUnlockableLocks = unlockableLocks.length > 0;
  const mustWaitForSmartLocksApi = Boolean(hasSmartLocksConfigured) && !smartLocksLoaded;

  if (mustWaitForSmartLocksApi && !hasPin && !hasKeybox) {
    return {
      view: SMART_LOCK_VIEW.LOADING,
      unlockableLocks: [],
      showSwipe: false,
      displayCode: '-',
    };
  }

  if (hasPin || hasUnlockableLocks) {
    return {
      view: SMART_LOCK_VIEW.SMART_LOCK,
      unlockableLocks,
      showSwipe: hasUnlockableLocks,
      displayCode: hasPin ? smartLockPinCode : '-',
    };
  }

  if (hasKeybox) {
    return {
      view: SMART_LOCK_VIEW.KEYBOX_ONLY,
      unlockableLocks: [],
      showSwipe: false,
      displayCode: keyboxCode,
    };
  }

  return {
    view: SMART_LOCK_VIEW.EMPTY,
    unlockableLocks: [],
    showSwipe: false,
    displayCode: '-',
  };
};

const SmartLock = (props) => {
  const { data: hotel } = useContext(HotelContext);
  const { smartLockPinCode, language, hasSmartLocks: hasSmartLocksConfigured } = useContext(UserContext);
  const { data: checkIn } = useContext(CheckInContext);
  const [smartLockId, setSmartLockId] = useState(null);
  const [smartLockName, setSmartLockName] = useState(null);
  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState(null);
  const [ unlockError, setUnlockError ] = useState(null);
  const [onlyOneSmartLock, setOnlyOneSmartLock] = useState(false);
  const { items: smartLocksAll, unlock } = useSmartLocks(checkIn.id);
  const swipeRef = useRef(null);

  const keyboxCode = useMemo(
    () => resolveKeyboxCode(checkIn?.placeholders, language),
    [checkIn?.placeholders, language]
  );

  const viewState = useMemo(() => resolveSmartLockViewState({
    smartLocksAll,
    smartLockPinCode,
    keyboxCode,
    hasSmartLocksConfigured,
  }), [smartLocksAll, smartLockPinCode, keyboxCode, hasSmartLocksConfigured]);

  const { view, unlockableLocks, showSwipe } = viewState;

  const displayCode = useMemo(() => {
    const code = viewState.displayCode;
    if (view === SMART_LOCK_VIEW.SMART_LOCK && smartLockPinCode) {
      return `${code}${isHostzArosa(hotel?.id) ? '#' : ''}`;
    }
    return code;
  }, [view, viewState.displayCode, smartLockPinCode, hotel?.id]);

  const isLoading = view === SMART_LOCK_VIEW.LOADING;
  const showContent = !isLoading;

  useEffect(() => {
    if (unlockableLocks.length === 1) {
      setOnlyOneSmartLock(true);
      setSmartLockId(unlockableLocks[0].id);
      setSmartLockName(unlockableLocks[0].name);
    }
  }, [unlockableLocks]);

  // useEffect(() => {
  //   console.log('2609 - smartLocksItemsGeneral', smartLocksItemsGeneral)
  //   let r = getByIds(smartLockIdsDB);
  //     if (r) {
  //       setSmartLocks(r)
  //     } else {
  //       setTimeout(() => {
  //         console.log('2609 - TO - smartLockIdsDB', smartLockIdsDB)
  //         r = getByIds(smartLockIdsDB);
  //         setSmartLocks(r)
  //       }, 5000);
  //     }
  // }, [ smartLockIdsDB, smartLocksItemsGeneral ])

  const SmartLockItem = ({ data, selected }) => {
    return (<li style={{ color: !selected ? "#666" : '#FFF', background: !selected ? "#FFF" : '#70a2d9', paddingTop: 10, paddingBottom: 10, paddingLeft: 10, paddingRight: 10, border: "2px solid #DDD", borderColor: !selected ? '#DDD' : '#255589', margin: 3, display: "inline-block", borderRadius: 8, cursor: "pointer", fontSize: 15 }} onClick={() => {
      if (selected) {
        setSmartLockId(null)
        setSmartLockName(null)
      } else {
        setSmartLockId(data.id)
        setSmartLockName(data.name)
        setUnlockError(null)
      }
    }}>{data.name || data.externalName}</li>)
  }

  useEffect(() => {
    if (!swipeRef.current) return;
    swipeRef.current.classList.remove('fade-enter-active');
    setOpening(false);
    setOpened(null);
    if (smartLockId) {
      setTimeout(() => {
        swipeRef.current?.classList.add('fade-enter-active');
      }, 200);
    }
  }, [swipeRef, smartLockId])


  return (
    <Screen
      section="smartlocks"
      title={'Smart Locks'}
    >
      {isLoading && <div className="loading text-center mt-5"><div className="spinner-border" role="status"></div></div>}
      {showContent && <div>
        {opened === null && !opening && <div style={{ height: 400, textAlign: "center", alignItems: "center", paddingTop: 40 }}>
          <div className="icon-circle" style={{
            margin: "auto", width: 300, height: 300, borderRadius: '50%', background: '#FFF', alignItems: "center",
            display: "block", boxShadow: '0 10px 20px #CCC'
          }}>
            <SmartLockIcon className="d-block-inline align-center align-bottom m_l-3" width={170} height={270} />
            <span className="d-block" style={{ color: '#FFF', background: '#444', borderRadius: 8, marginTop: -30, marginLeft: 40, marginRight: 40, padding: 8 }}>
              <span className="font-weight-light" style={{ marginRight: 10 }}>PIN CODE</span>
              <span className="font-weight-bold">{displayCode}</span>
            </span>
            {isHostzArosa(hotel?.id) && smartLockPinCode && <span className="d-block" style={{ borderRadius: 8, marginLeft: 40, marginRight: 40, padding: 8, backgroundColor: '#E0E0E0', marginBottom: 10, fontStyle: 'italic', fontSize: 14 }}>
              {T('smartlocks.pin-code-arosa-explanation')}
            </span>}
          </div>
        
        </div>}
        {showSwipe && !smartLockId && !unlockError && <p className="text-center mb-4">{T('checkin.select-door-open')}</p>}
        {showSwipe && unlockableLocks.length > 0 && !onlyOneSmartLock && !opening && opened === null && !unlockError && <ul style={{ listStyle: "none", margin: 0, padding: 0, textAlign: "center" }}>
          {unlockableLocks.map((sm, idx) => (<SmartLockItem key={`smartLocks-item-${idx}`} data={sm} selected={sm.id === smartLockId} />))}
        </ul>}
      </div>}
      {smartLockId && <div className="text-center" style={{ marginTop: 40, marginLeft: 40, marginRight: 40 }}>
        {/* <p className="mb-2">Your access PIN is:</p>
        <h1 className="font-weight-bolder">123456</h1> */}
        {opening && <Player
          // ref={this.player} // set the ref to your class instance
          autoplay={true}
          loop={true}
          src="/lottie/loading.json"
          style={{ height: '500px', width: '200px' }}
        ></Player>}
        {opened && <Player
          // ref={this.player} // set the ref to your class instance
          autoplay={true}
          loop={false}
          keepLastFrame={true}
          src="/lottie/success.json"
          style={{ height: '500px', width: '300px' }}
        ></Player>}
      </div>}
      {showSwipe && <div ref={swipeRef} className="fade-enter">
        {!unlockError && <div style={{ height: 50, marginBottom: 20 }}>
          <ReactSwipeButton
            // text='SWIPE TO OPEN'
            text={T("smartlocks.swipe-to-open")}
            text_unlocked={opened ? 'OPEN' : unlockError ? 'ERROR' : 'OPENING...'}
            color={opened ? '#0d8e24' : unlockError ? 'red' : '#0597c6'}
            onSuccess={() => {
              setUnlockError(null);
              setOpening(true);
              unlock(smartLockId).then(r => {
                console.log('18ago - rrrrr', r)
                setOpening(false);
                if (r?.result) {
                  setOpened(true);
                  setTimeout(() => {
                    window.location.reload()
                  }, 3000);
                } else {
                  setOpened(null);
                  setUnlockError(r?.reason || "Error");
                }
              })
              // setTimeout(() => {
              //   setOpening(false);
              //   setOpened(true)
              //   setTimeout(() => {
              //     // setSmartLockId(null);
              //     // setOpening(false);
              //     // setOpened(false)
              //     window.location.reload()
              //   }, 3000);
              // }, 2000); 
            }}
          />
        </div>}
        {unlockError && <div style={{ padding: 10, textAlign: "center", color: "red" }}>
          <h6 className="mb-0" style={{ color: "red", fontWeight: "bold" }}>{smartLockName}</h6>
          <p>{unlockError}</p>
          <button style={{ cursor: "pointer", fontSize: 15, background: "white", color: "#888", border: "1px solid #DDD", borderRadius: 10, padding: "8px 25px" }} onClick={() => {
            setUnlockError(null)
            setOpening(null)
            setOpened(null)
            setSmartLockId(null)
            setSmartLockName(null)
          }}>Back</button>
        </div>}
      </div>}
    </Screen>
  );
}

export default SmartLock;